import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { setUnauthorizedHandler } from '../api'

const AUTH_STORAGE_KEY = 'pit_jwt'
const AUTH_GLOBAL_KEY = '__PIT_JWT__'
const AUTH_MESSAGE_TYPES = ['pit-auth:update', 'pit-auth:refresh']
const SESSION_EXPIRED_KEY = 'pit_session_expired_notice'

type AuthContextValue = {
  token: string | null
  ready: boolean
  isAuthenticated: boolean
  refresh: () => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const resolveAuthToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const globalCandidate = (window as Record<string, unknown>)[AUTH_GLOBAL_KEY]
  if (typeof globalCandidate === 'string' && globalCandidate.length > 0) {
    return globalCandidate
  }

  const stores: Array<Storage | undefined> = [window.sessionStorage, window.localStorage]
  for (const store of stores) {
    if (!store) continue
    try {
      const value = store.getItem(AUTH_STORAGE_KEY)
      if (typeof value === 'string' && value.length > 0) {
        return value
      }
    } catch {
      // Access denied (private mode or blocked). Ignore and keep checking.
    }
  }

  return null
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  const clearStoredToken = useCallback(() => {
    try {
      window.localStorage.removeItem(AUTH_STORAGE_KEY)
      window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
      if (AUTH_GLOBAL_KEY in window) {
        // @ts-expect-error clear global escape hatch
        window[AUTH_GLOBAL_KEY] = null
      }
    } catch {
      // ignore storage failures
    }
  }, [])

  const refresh = useCallback(() => {
    const nextToken = resolveAuthToken()
    setToken(nextToken)
    setReady(true)
  }, [])

  const handleSessionExpired = useCallback((message?: string) => {
    const notice = message?.trim() || 'Session expired. Please log in again.'
    try {
      window.sessionStorage.setItem(SESSION_EXPIRED_KEY, notice)
    } catch {
      // ignore storage write issues
    }

    clearStoredToken()
    setToken(null)
    setReady(true)

    try {
      if (typeof window !== 'undefined') {
        // Lightweight surface so users see why they were bumped.
        if (typeof window.alert === 'function') {
          window.alert(notice)
        }
        const next = `${window.location.pathname}${window.location.search}`
        const target = `/login?next=${encodeURIComponent(next)}`
        window.location.assign(target)
      }
    } catch {
      // swallow navigation errors
    }
  }, [clearStoredToken])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    setUnauthorizedHandler((info) => handleSessionExpired(info?.message))
    return () => setUnauthorizedHandler(null)
  }, [handleSessionExpired])

  const logout = useCallback(() => {
    clearStoredToken()
    setToken(null)
    setReady(true)
  }, [clearStoredToken])

  useEffect(() => {
    const handleStorage = () => refresh()
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }
    const handleMessage = (event: MessageEvent) => {
      if (event?.data && typeof event.data === 'object' && 'type' in event.data) {
        const { type } = event.data as { type?: string }
        if (type && AUTH_MESSAGE_TYPES.includes(type)) {
          refresh()
        }
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', handleStorage)
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', handleStorage)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('message', handleMessage)
    }
  }, [refresh])

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      ready,
      isAuthenticated: Boolean(token),
      refresh,
      logout,
    }),
    [token, ready, refresh, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
